import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { HelpTooltip } from "./shared/HelpSystem";

const GV = {
  vert:"#2D7A2D", vertClair:"#00A651", vertLight:"#E8F5E9", vertMid:"#4CAF50",
  vertDark:"#1B5E20", blanc:"#FFFFFF", gris:"#F5F5F5", grisMid:"#757575",
  dark:"#1A1A1A", border:"#E0E0E0", orange:"#E8601A", rouge:"#DC2626", bleu:"#1565C0",
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// Mapping ID solution PageSolution → clé comparaison
const SOL_ID_VERS_COMP = {
  "deux_murs_cantilever": "murs_cantilever",
  "mur_unique_hauteur":   "murs_cantilever",
  "murs_cantilever":      "murs_cantilever",
  "deux_murs_gabions":    "gabions",
  "gabions":              "gabions",
  "pentes_clouees":       "pentes_clouees",
  "pentes_clouees_talus": "pentes_clouees",
  "ecrans":               "ecrans",
  "talutage":             "talutage",
  "pieux_secants":        "pieux_secants",
  "pieux":                "pieux",
  "pieux_stab":           "pieux",
  "semelles":             "semelles",
  "micropieux":           "micropieux",
  "stabilite_pentes":     "pentes_clouees",
  "tassements":           "semelles",

  // Ajouts Phase Coût-2c-bis — normalisation IDs backend + top-3 frontend (validés équipe)
  // Soutènement — variantes d'écrans et de murs
  "mur_cantilever":       "murs_cantilever",
  "paroi_moldee":         "ecrans",
  "ecran_ancre":          "ecrans",
  "ecran_berlinois":      "ecrans",
  "palplanches":          "ecrans",
  "deux_murs_gradins":    "murs_cantilever",
  "soutenement_clouage":  "pentes_clouees",
  "mur_prefabrique":      "murs_cantilever",
  // Fondation — GAP-SOL-FONDATION résolu
  "semelles_superficielles": "semelles",
  "pieux_beton":          "pieux",
  "pieux_fores":          "pieux",
  // massif_renforce et radier_general NON mappés (GAP métier — décision équipe ultérieure)
};

// Catalogue comparaison — soutènement + fondations
const CATALOGUE_COMP = {
  murs_cantilever: { nom:"Mur Cantilever BA",            icone:"🏗️", couleur:"#2563eb", range:"H opt. 2–8m" },
  murs_poids:      { nom:"Mur Poids Cyclopéen",          icone:"🪨",  couleur:"#d97706", range:"H opt. 1–4m" },
  gabions:         { nom:"Mur Gabions",                   icone:"🧱",  couleur:"#16a34a", range:"H opt. 1–6m" },
  pentes_clouees:  { nom:"Pentes Clouées / Soil Nailing", icone:"⚓",  couleur:"#7c3aed", range:"H opt. 3–15m" },
  ecrans:          { nom:"Écrans de Soutènement",         icone:"🏛️", couleur:"#0891b2", range:"H opt. 4–20m+" },
  talutage:        { nom:"Talutage / Stabilisation",      icone:"⛰️", couleur:"#65a30d", range:"H opt. 1–8m" },
  pieux_secants:   { nom:"Paroi de Pieux Sécants",        icone:"🏙️", couleur:"#dc2626", range:"H opt. 4–20m+" },
  pieux:           { nom:"Fondation sur Pieux Forés",     icone:"⚓",  couleur:"#9333ea", range:"Profondeur 8–30m" },
  semelles:        { nom:"Semelles Superficielles",        icone:"🏢",  couleur:"#0891b2", range:"Prof. ancrage ≥ 0.8m" },
  micropieux:      { nom:"Micropieux (reprise / renfort)", icone:"🔩",  couleur:"#b45309", range:"Profondeur 8–25m" },
};


// FILTRE_PAR_OUVRAGE — famille métier → solutions du catalogue compatibles.
// Sources : PageProjet.jsx L269-274, casTypiques.js L97-104, fr.json L482-487,
//           diagnostic_facts.py L43-97, matrice_non_regression.md.
const FILTRE_PAR_OUVRAGE = {
  "fondation_batiment":     ["semelles", "pieux", "micropieux"],
  "soutenement_remblai":    ["murs_cantilever", "murs_poids", "gabions", "pentes_clouees", "ecrans", "talutage", "pieux_secants"],
  "soutenement_deblai":     ["ecrans", "pieux_secants", "murs_cantilever", "gabions"],
  "talus_stabilisation":    ["pentes_clouees", "talutage", "gabions"],
  "amelioration_sol":       [],
  "infrastructure_lineaire":["semelles", "pieux", "pentes_clouees", "ecrans", "talutage"],
  "ouvrage_art":            ["pieux", "pieux_secants", "ecrans", "micropieux"],
};

// Familles CATALOGUE_COMP regroupant des technologies techniquement distinctes
// (matériaux différents, procédés d'exécution différents, normes différentes).
// Mapping famille → clé i18n du texte explicatif pour le MO.
// Identifié par audit Coût-2d (d3ea997), étape 0 Coût-2d-β.
// Extension : ajouter une entrée ici sans modifier le JSX.
const FAMILLES_TECHNOLOGIES_DISTINCTES = {
  ecrans: "comparaison_page.ecrans_technologies",
};

// Mapping nature sol → qualité
const SOL_NATURE_MAP = {
  remblai:"mauvais", argile_molle:"mauvais", argile_ferme:"moyen", argile_raide:"moyen",
  limon:"mauvais", sable_lache:"mauvais", sable_moyen:"bon", sable_dense:"bon",
  gravier:"bon", marne:"bon", calcaire_altere:"bon", calcaire_sain:"tres_bon", roche:"tres_bon",
  argile:"moyen", sable:"bon",
};

const SOL_OPTIONS = [
  { val:"remblai",         label:"Remblai",                 qual:"mauvais" },
  { val:"argile_molle",    label:"Argile molle",            qual:"mauvais" },
  { val:"argile_ferme",    label:"Argile ferme",            qual:"moyen"   },
  { val:"argile_raide",    label:"Argile raide",            qual:"moyen"   },
  { val:"limon",           label:"Limon",                   qual:"mauvais" },
  { val:"sable_lache",     label:"Sable lâche",             qual:"mauvais" },
  { val:"sable_moyen",     label:"Sable moyen",             qual:"bon"     },
  { val:"sable_dense",     label:"Sable dense",             qual:"bon"     },
  { val:"gravier",         label:"Gravier",                 qual:"bon"     },
  { val:"marne",           label:"Marne",                   qual:"bon"     },
  { val:"calcaire_altere", label:"Calcaire altéré",         qual:"bon"     },
  { val:"calcaire_sain",   label:"Calcaire sain",           qual:"tres_bon"},
  { val:"roche",           label:"Roche",                   qual:"tres_bon"},
];

// Coût-2e-α — règle produit globale : 3 modes d'affichage selon resultats.length
const getModeComparaison = (resultats) => {
  if (!Array.isArray(resultats) || resultats.length === 0) return 'non_disponible';
  if (resultats.length === 1) return 'fiche_unique';
  return 'comparaison';
};

const getSolutionsNonComparees = (classementBackend, typesAffichees) => {
  if (!Array.isArray(classementBackend)) return [];
  return classementBackend.filter((sol) => {
    const cleComp = SOL_ID_VERS_COMP[sol.id];
    if (!cleComp) return true;
    if (!Array.isArray(typesAffichees) || !typesAffichees.includes(cleComp)) return true;
    return false;
  });
};

const getNomSolution = (solution) => {
  if (!solution) return '';
  if (solution.nom) return solution.nom;
  if (solution.label) return solution.label;
  if (solution.titre) return solution.titre;
  if (solution.label_fr) return solution.label_fr;
  if (solution.id) {
    return solution.id
      .split('_')
      .map(mot => mot.charAt(0).toUpperCase() + mot.slice(1))
      .join(' ');
  }
  return '';
};

function TabComparaison({ donneesProjet, pays: paysProp, diagnosticBackend = null, onUpdateComparaisonPrix = () => {}, onUpdateComparaisonParams = () => {} }) {
  const { t } = useTranslation();
  const pays = paysProp || donneesProjet?.clientInfo?.pays || "france";
  const isEuropeNorm = pays === "france" || pays === "eu";
  const devise = "EUR";

  // Dériver les types à comparer — priorisation diagnosticBackend (H1+H2+H3 Coût-2c-bis)
  const classementBackend = diagnosticBackend?.adequation?.classement || [];
  const getMotifNonComparaison = (solution) => {
    // Détection complémentaire : niveau backend ("COMPLÉMENTAIRE" via niveau_force) OU type_solution
    const estComplementaire =
      (typeof solution.niveau === 'string' && solution.niveau.toUpperCase().includes('COMPLÉMENTAIRE')) ||
      solution.type_solution === 'complementaire';
    if (estComplementaire) return t('comparaison_page.motif_complementaire');
    if (!SOL_ID_VERS_COMP[solution.id]) return t('comparaison_page.motif_non_couvert');
    return t('comparaison_page.motif_modele_different');
  };
  const typesProjet = (() => {
    // Source 1 : diagnosticBackend.adequation.classement (même source que PageSolution)
    if (classementBackend.length > 0) {
      const idsMappes = [...new Set(
        classementBackend.map(s => SOL_ID_VERS_COMP[s.id]).filter(Boolean)
      )];
      // Q2=β : classement présent mais aucun ID mappable → vide (non_disponible, ne masque pas un bug de mapping)
      return idsMappes.length > 0 ? idsMappes : [];
    }
    // Source 2 : donneesProjet.solutions (fallback Q1=α)
    if (!donneesProjet?.solutions?.length) return null;
    const cles = [...new Set(
      donneesProjet.solutions.map(s => SOL_ID_VERS_COMP[s.id]).filter(Boolean)
    )];
    return cles.length > 0 ? cles : null;
  })();

  const typeOuvrageProjet = donneesProjet?.typeOuvrage;
  const modeAffichage = (() => {
    if (!typeOuvrageProjet) return "demonstration";
    if (typesProjet && typesProjet.length > 0) {
      const famillesAutorisees = FILTRE_PAR_OUVRAGE[typeOuvrageProjet] || [];
      const intersection = typesProjet.filter(t => famillesAutorisees.includes(t));
      return intersection.length > 0 ? "normal" : "non_disponible";
    }
    const solutionsBackend = donneesProjet?.solutions;
    if (Array.isArray(solutionsBackend) && solutionsBackend.length > 0) return "non_disponible"; // C1
    const famille = FILTRE_PAR_OUVRAGE[typeOuvrageProjet];
    if (famille && famille.length > 0) return "fallback_famille"; // C2
    return "non_disponible";
  })();

  // Couche d'assise la plus profonde
  const _coucheAssise = (() => {
    const couches = donneesProjet?.couches || [];
    return couches.slice().reverse().find(c => c.nature && !["remblai","terre_vegetale"].includes(c.nature)) || couches[couches.length - 1];
  })();

  const initParams = () => {
    const defZone = isEuropeNorm ? "2" : "IV";
    if (!donneesProjet) return { hauteur:4, longueur:20, zone_sismique:defZone, sol:"sable_moyen", nappe:false, priorite:"equilibre" };
    const H = parseFloat(donneesProjet.hauteur) || 4;
    const solNature = _coucheAssise?.nature || donneesProjet.typeSol || "sable_moyen";
    const zone = donneesProjet.zoneSismique || defZone;
    return {
      hauteur:       Math.min(H, 15),
      longueur:      parseFloat(donneesProjet.longueur) || 50,
      zone_sismique: zone,
      sol:           solNature,
      nappe:         ["haute","moderee","profonde"].includes(donneesProjet.nappe),
      priorite:      donneesProjet.priorite || "equilibre",
    };
  };

  const [params, setParams] = useState(() => donneesProjet?.comparaisonParams || initParams());
  const [prixUnitaires, setPrixUnitaires] = useState(() => donneesProjet?.comparaisonPrix ? { ...donneesProjet.comparaisonPrix } : {});
  const [editeurPrixOuvert, setEditeurPrixOuvert] = useState(false);
  const [resultats, setResultats] = useState(null);
  const [calcule, setCalcule] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreurApi, setErreurApi] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [apiPrixRef, setApiPrixRef] = useState(() => donneesProjet?.comparaisonPrix || {});
  const projetActif = !!donneesProjet;

  useEffect(() => {
    if (projetActif) {
      setParams(donneesProjet?.comparaisonParams || initParams());
      setPrixUnitaires(donneesProjet?.comparaisonPrix ? { ...donneesProjet.comparaisonPrix } : {});
      setApiData(null);
      setApiPrixRef(donneesProjet?.comparaisonPrix || {});
      setResultats(null);
      setCalcule(false);
      setErreurApi(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donneesProjet?._projetId]);

  useEffect(() => {
    if (apiData && calcule && Object.keys(apiPrixRef).length > 0) {
      setResultats(calculerResultats(apiData.solutions, apiPrixRef, prixUnitaires, params, modeAffichage, typeOuvrageProjet, typesProjet));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.priorite, prixUnitaires]);

  const sp = (k, v) => setParams(p => ({ ...p, [k]: v }));

  const handleParamChange = (champ, valeur) => {
    const nouvelParams = { ...params, [champ]: valeur };
    setParams(nouvelParams);
    onUpdateComparaisonParams(nouvelParams);
  };

  const handleParamBlur = (champ, valeur) => {
    const nouvelParams = { ...params, [champ]: valeur };
    setParams(nouvelParams);
    onUpdateComparaisonParams(nouvelParams);
  };

  const handlePrixBlur = (type, valeur) => {
    const nouveauxPrix = { ...prixUnitaires, [type]: parseFloat(valeur) || apiPrixRef[type] || 1000 };
    setPrixUnitaires(nouveauxPrix);
    onUpdateComparaisonPrix(nouveauxPrix);
  };

  const handleReinitialiserPrix = () => {
    const prixDefaut = { ...apiPrixRef };
    setPrixUnitaires(prixDefaut);
    onUpdateComparaisonPrix(prixDefaut);
  };

  const handleCalculer = async () => {
    onUpdateComparaisonParams(params);
    setLoading(true);
    setErreurApi(null);
    try {
      const token = localStorage.getItem("geo5_token");
      const resp = await fetch(`${API_URL}/comparaison`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          typeOuvrage:   typeOuvrageProjet || null,
          hauteur:       params.hauteur,
          longueur:      params.longueur,
          zone_sismique: params.zone_sismique,
          sol:           params.sol,
          nappe:         params.nappe,
          priorite:      params.priorite,
          country:       "FR",
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const H = params.hauteur;
      const prixRef = {};
      Object.entries(data.solutions).forEach(([sol, s]) => {
        prixRef[sol] = sol === "semelles" ? s.cout_ml : Math.round(s.cout_ml * 4 / H);
      });
      setApiData(data);
      setApiPrixRef(prixRef);
      setPrixUnitaires({ ...prixRef });
      onUpdateComparaisonPrix({ ...prixRef });
      setResultats(calculerResultats(data.solutions, prixRef, prixRef, params, modeAffichage, typeOuvrageProjet, typesProjet));
      setCalcule(true);
    } catch {
      setErreurApi("Service de comparaison indisponible — réessayez");
    } finally {
      setLoading(false);
    }
  };

  const budget = parseFloat(donneesProjet?.clientInfo?.budget) || 0;
  const coutMin = resultats ? Math.min(...resultats.map(s => s.cout_total)) : 0;
  const depassementBudget = budget > 0 && calcule && coutMin > budget * 1.4
    ? { coutMin, pct: Math.round((coutMin / budget - 1) * 100) }
    : null;

  const inpSt = { width:"100%", padding:"10px 12px", fontSize:13, border:`1.5px solid ${GV.border}`, borderRadius:8, color:GV.dark, background:GV.blanc, outline:"none", boxSizing:"border-box" };
  const lblSt = { fontSize:11, fontWeight:700, color:GV.grisMid, display:"block", marginBottom:5, letterSpacing:0.8, textTransform:"uppercase" };

  const getCatalogueNom = (type) => {
    const key = `comparaison_page.catalogue.${type}`;
    const translated = t(key, { defaultValue: "" });
    return translated || CATALOGUE_COMP[type]?.nom || type;
  };

  const moduleTag = (type) => {
    const info = CATALOGUE_COMP[type];
    if (!info) return null;
    const norm = pays === "eu" ? "EN 1997" : pays === "france" ? "NF P 94" : "GEO5";
    return <span style={{ background:"#E3F2FD", border:`1px solid ${info.couleur}`, borderRadius:99, padding:"2px 10px", fontSize:10, color:info.couleur, fontWeight:700 }}>✅ {norm} — {getCatalogueNom(type)}</span>;
  };

  // Zones sismiques EC8 (FR/EU uniquement — branche RPA supprimée, marché DZ non servi)
  const zonesSismiques = t("comparaison_page.zones_ec8", { returnObjects: true })
    || [["1","Zone 1"],["2","Zone 2"],["3","Zone 3"],["4","Zone 4"],["5","Zone 5"]];
  const labelZone = t("comparaison_page.zone_ec8");
  const normeRef  = pays === "eu" ? t("comparaison_page.norme_ref_eu") : t("comparaison_page.norme_ref_fr");

  const mode = getModeComparaison(resultats);

  return (
    <div style={{ fontFamily:"'Segoe UI', system-ui, sans-serif", background:GV.blanc, minHeight:"100vh" }}>

      {/* HEADER */}
      <header style={{ background:GV.blanc, borderBottom:`3px solid ${GV.vert}`, padding:"12px 48px", textAlign:"center", boxShadow:"0 2px 8px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize:20, fontWeight:900, color:GV.vert }}>
          {mode === 'fiche_unique' ? t("comparaison_page.titre_fiche_unique") : t("comparaison_page.titre")}
        </div>
        <div style={{ fontSize:11, color:GV.grisMid, letterSpacing:2, textTransform:"uppercase", marginTop:2 }}>
          {t("comparaison_page.sous_titre")} {normeRef}
        </div>
      </header>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 24px" }}>

        {/* ── Bandeau projet ── */}
        <div style={{ background: projetActif ? GV.vertLight : "#F9FAFB", border:`1.5px solid ${projetActif ? GV.vertMid : GV.border}`, borderRadius:10, padding:"12px 18px", marginBottom:20 }}>
          <div style={{ display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
            <span style={{ fontSize:18 }}>🔗</span>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:800, color:GV.vertDark }}>
                {projetActif ? "Projet hérité — données synchronisées" : "Aucun projet analysé — saisie manuelle"}
              </div>
              <div style={{ fontSize:12, color:GV.grisMid, marginTop:4, display:"flex", gap:12, flexWrap:"wrap" }}>
                <span>📐 H = <strong>{params.hauteur} m</strong></span>
                <span>🌍 Zone <strong>{params.zone_sismique}</strong> EC8</span>
                <span>🪨 Sol : <strong>{SOL_OPTIONS.find(s=>s.val===params.sol)?.label || params.sol}</strong></span>
                {params.nappe && <span style={{ color:GV.orange }}>💧 Nappe présente <strong>(+15%)</strong></span>}
                {donneesProjet?.longueur && <span>📏 L = <strong>{params.longueur} m</strong></span>}
                {donneesProjet?.rdt && <span>⚡ Rdt = <strong>{donneesProjet.rdt} kPa</strong></span>}
                {donneesProjet?.clientInfo?.codeRef && (
                  <span style={{ fontFamily:"monospace", color:"#6A1B9A", fontWeight:700 }}>
                    🏷 {donneesProjet.clientInfo.codeRef}
                  </span>
                  )}
              </div>
            </div>
          </div>
          {typesProjet && (
            <div style={{ marginTop:10, display:"flex", gap:8, flexWrap:"wrap", alignItems:"center" }}>
              <span style={{ fontSize:11, color:GV.vertDark, fontWeight:700 }}>Solutions à comparer :</span>
              {typesProjet.map(type => {
                const info = CATALOGUE_COMP[type];
                return info ? (
                  <span key={type} style={{ background:GV.blanc, border:`1.5px solid ${info.couleur}`, borderRadius:99, padding:"3px 12px", fontSize:11, color:info.couleur, fontWeight:700 }}>
                    {info.icone} {info.nom}
                  </span>
                ) : null;
              })}
            </div>
          )}
        </div>

        {/* ── Bandeau mode démonstration ── */}
        {modeAffichage === "demonstration" && (
          <div style={{ background:"#EFF6FF", border:"1.5px solid #3B82F6", borderRadius:10, padding:"12px 18px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:12 }}>
            <span style={{ fontSize:20, flexShrink:0 }}>🔍</span>
            <div>
              <div style={{ fontWeight:800, fontSize:13, color:"#1E40AF", marginBottom:3 }}>Mode démonstration — aucun projet chargé</div>
              <div style={{ fontSize:12, color:"#1D4ED8", lineHeight:1.6 }}>Les résultats affichés couvrent l'ensemble du catalogue. Chargez un projet analysé pour obtenir une comparaison ciblée sur les solutions recommandées.</div>
            </div>
          </div>
        )}

        {/* ── Bandeau fallback famille ── */}
        {modeAffichage === "fallback_famille" && (
          <div style={{ background:"#FFFBEB", border:"1.5px solid #F59E0B", borderRadius:10, padding:"12px 18px", marginBottom:16, display:"flex", alignItems:"flex-start", gap:12 }}>
            <span style={{ fontSize:20, flexShrink:0 }}>📂</span>
            <div>
              <div style={{ fontWeight:800, fontSize:13, color:"#92400E", marginBottom:3 }}>Comparaison indicative par famille d'ouvrage</div>
              <div style={{ fontSize:12, color:"#78350F", lineHeight:1.6 }}>Aucune solution spécifique n'a encore été sélectionnée pour ce projet. La comparaison porte sur les solutions compatibles avec la famille d'ouvrage détectée.</div>
            </div>
          </div>
        )}

        {/* Formulaire */}
        <div style={{ background:GV.gris, borderRadius:14, padding:"22px 24px", marginBottom:24, border:`1px solid ${GV.border}` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={{ color:GV.vertDark, fontSize:12, fontWeight:800, letterSpacing:2, textTransform:"uppercase" }}>Paramètres du projet</div>
            <button onClick={() => setEditeurPrixOuvert(v => !v)}
              style={{ background:"none", border:`1px solid ${GV.border}`, borderRadius:8, padding:"6px 12px", fontSize:12, cursor:"pointer", color:GV.grisMid, display:"flex", alignItems:"center", gap:6 }}>
              ⚙️ {editeurPrixOuvert ? "Masquer les prix" : "Adapter les prix à votre région"}
            </button>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(155px, 1fr))", gap:14, marginBottom:16 }}>
            <div>
              <label style={lblSt}>Hauteur H (m)</label>
              <input type="number" min="1" max="20" step="0.5" value={params.hauteur} onChange={e=>sp("hauteur",parseFloat(e.target.value)||4)} onBlur={e=>handleParamBlur("hauteur",parseFloat(e.target.value)||4)} style={inpSt}/>
            </div>
            <div>
              <label style={lblSt}>Longueur L (m)</label>
              <input type="number" min="5" max="1000" step="5" value={params.longueur} onChange={e=>sp("longueur",parseFloat(e.target.value)||20)} onBlur={e=>handleParamBlur("longueur",parseFloat(e.target.value)||20)} style={inpSt}/>
            </div>
            <div>
              <label style={lblSt}>{labelZone}</label>
              <select value={params.zone_sismique} onChange={e=>handleParamChange("zone_sismique",e.target.value)} style={{...inpSt,background:GV.blanc}}>
                {zonesSismiques.map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={lblSt}>Sol de fondation</label>
              <select value={params.sol} onChange={e=>handleParamChange("sol",e.target.value)} style={{...inpSt,background:GV.blanc}}>
                {SOL_OPTIONS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lblSt}>Priorité</label>
              <select value={params.priorite} onChange={e=>handleParamChange("priorite",e.target.value)} style={{...inpSt,background:GV.blanc}}>
                <option value="securite">Sécurité maximale</option>
                <option value="equilibre">Équilibre budget / fiabilité</option>
                <option value="economie">Économie maximale</option>
              </select>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:16, marginBottom:16 }}>
            <label style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer", fontSize:13, color:GV.dark, fontWeight:600 }}>
              <input type="checkbox" checked={params.nappe} onChange={e=>handleParamChange("nappe",e.target.checked)} style={{ width:16, height:16, accentColor:GV.vert }}/>
              Eau souterraine présente <span style={{ fontSize:11, color:GV.orange, fontWeight:700 }}>(+15% estimé)</span> <HelpTooltip content="La présence de nappe implique des sujétions supplémentaires : drainage, étanchéité, rabattement — majoration forfaitaire de 15% sur le coût estimé."/>
            </label>
          </div>
          <button onClick={handleCalculer} disabled={loading} style={{ background:loading?GV.grisMid:`linear-gradient(135deg,${GV.vert},${GV.vertClair})`, color:GV.blanc, border:"none", borderRadius:10, padding:"12px 28px", fontSize:14, fontWeight:800, cursor:loading?"not-allowed":"pointer", boxShadow:`0 4px 14px ${GV.vert}40`, opacity:loading?0.7:1 }}>
            {loading ? "⏳ Calcul en cours…" : t("comparaison_page.calculer")}
          </button>
          {erreurApi && (
            <div style={{ background:"#FEF2F2", border:"1.5px solid #EF4444", borderRadius:8, padding:"10px 16px", marginTop:12, fontSize:13, color:"#991B1B" }}>
              ⚠ {erreurApi}
            </div>
          )}
        </div>

        {/* Alerte budget */}
        {depassementBudget && (
          <div style={{ background:"#FEF3C7", border:"2px solid #F59E0B", borderRadius:10, padding:"14px 20px", marginBottom:20, display:"flex", alignItems:"flex-start", gap:14 }}>
            <span style={{ fontSize:22, flexShrink:0 }}>⚠️</span>
            <div>
              <div style={{ fontWeight:800, fontSize:14, color:"#92400E", marginBottom:4 }}>
                {t("comparaison_page.budget_depasse_titre")} — +{depassementBudget.pct}%
              </div>
              <div style={{ fontSize:13, color:"#78350F", lineHeight:1.6 }}>
                <strong>{depassementBudget.coutMin.toLocaleString("fr-FR")} {devise}</strong>
                {" "}(+{depassementBudget.pct}%) / <strong>{budget.toLocaleString("fr-FR")} {devise}</strong>
              </div>
            </div>
          </div>
        )}
        {budget > 0 && calcule && !depassementBudget && (
          <div style={{ background:"#F0FDF4", border:"1.5px solid #4CAF50", borderRadius:10, padding:"12px 18px", marginBottom:20, display:"flex", alignItems:"center", gap:12 }}>
            <span style={{ fontSize:18 }}>✅</span>
            <div style={{ fontSize:13, color:"#1B5E20", fontWeight:600 }}>
              {t("comparaison_page.budget_ok")} {budget.toLocaleString("fr-FR")} {devise}.
            </div>
          </div>
        )}

        {/* ── Éditeur de prix unitaires ── */}
        {editeurPrixOuvert && (
          <div style={{ background:"#F8FAFC", border:`1.5px solid ${GV.border}`, borderRadius:12, padding:"18px 22px", marginBottom:20 }}>
            <div style={{ fontSize:12, fontWeight:800, color:GV.vertDark, textTransform:"uppercase", letterSpacing:1.5, marginBottom:14 }}>
              ⚙️ Prix unitaires €/ml — référence H=4m (fourniture + pose)
            </div>
            {Object.keys(prixUnitaires).length === 0 ? (
              <div style={{ fontSize:12, color:GV.grisMid, padding:"8px 0" }}>Calculez d'abord une comparaison pour adapter les prix.</div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(200px, 1fr))", gap:10 }}>
                {Object.entries(prixUnitaires).map(([type, current]) => {
                  const info = CATALOGUE_COMP[type];
                  if (!info) return null;
                  const defaut = apiPrixRef[type] || current;
                  return (
                    <div key={type} style={{ display:"flex", alignItems:"center", gap:8, background:GV.blanc, borderRadius:8, padding:"8px 12px", border:`1px solid ${GV.border}` }}>
                      <span style={{ fontSize:16, flexShrink:0 }}>{info.icone}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:GV.dark, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{info.nom}</div>
                        <div style={{ fontSize:10, color:GV.grisMid }}>API : {defaut} €/ml</div>
                      </div>
                      <input
                        type="number" min="50" max="20000" step="50"
                        value={current}
                        onChange={e => setPrixUnitaires(p => ({ ...p, [type]: parseFloat(e.target.value) || defaut }))}
                        onBlur={e => handlePrixBlur(type, e.target.value)}
                        style={{ width:80, padding:"4px 6px", fontSize:12, border:`1px solid ${GV.vert}`, borderRadius:6, textAlign:"right", outline:"none" }}
                      />
                      <span style={{ fontSize:11, color:GV.grisMid }}>€</span>
                    </div>
                  );
                })}
              </div>
            )}
            <button onClick={handleReinitialiserPrix} disabled={Object.keys(apiPrixRef).length === 0}
              style={{ marginTop:12, background:"none", border:`1px solid ${GV.border}`, borderRadius:6, padding:"5px 14px", fontSize:11, cursor:"pointer", color:GV.grisMid }}>
              ↺ Réinitialiser aux prix API
            </button>
          </div>
        )}

        {/* ── Message non disponible ── */}
        {modeAffichage === "non_disponible" && (
          <div style={{ background:"#FEF2F2", border:"2px solid #EF4444", borderRadius:12, padding:"28px 32px", marginBottom:20, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>🚫</div>
            <div style={{ fontWeight:800, fontSize:15, color:"#991B1B", marginBottom:10 }}>Comparaison non disponible pour ce type d'ouvrage</div>
            <div style={{ fontSize:13, color:"#7F1D1D", lineHeight:1.7, maxWidth:560, margin:"0 auto" }}>
              Les solutions recommandées pour ce projet n'appartiennent pas aux familles couvertes par l'outil de comparaison budgétaire actuel.
              Consultez les fiches détaillées dans la page Solutions ou adressez-vous à un bureau d'études géotechniques.
            </div>
          </div>
        )}

        {/* Résultats */}
        {modeAffichage !== "non_disponible" && calcule && resultats && (<>

          {/* ── Bandeau disclaimer scores ── */}
          <div style={{ backgroundColor:"#f9fafb", borderLeft:"3px solid #6b7280", borderRadius:4, padding:"12px 16px", marginBottom:16, fontSize:13, color:"#374151", lineHeight:1.5 }}>
            {apiData?.disclaimer || t("comparaison_page.disclaimer_scores")}
          </div>

          {/* ── Bandeau fiche unique Coût-2e-α ── */}
          {mode === 'fiche_unique' && (
            <div style={{ backgroundColor:"#FFF7ED", border:"1.5px solid #F59E0B", borderRadius:10, padding:"14px 18px", marginBottom:20, fontSize:13, color:"#92400E" }}>
              <div style={{ fontWeight:800, marginBottom:8 }}>{t("comparaison_page.bandeau_non_comparees")}</div>
              <ul style={{ margin:0, paddingLeft:18, lineHeight:1.8 }}>
                {getSolutionsNonComparees(classementBackend, resultats.map(r => r.type)).map((sol) => (
                  <li key={sol.id}><strong>{getNomSolution(sol)}</strong> — {getMotifNonComparaison(sol)}</li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Graphique à barres SVG ── */}
          {(() => {
            const maxCout = Math.max(...resultats.map(s => s.cout_total));
            const W = 820, barH = 36, gap = 10, paddingL = 200, paddingR = 80;
            const svgH = resultats.length * (barH + gap) + 40;
            return (
              <div style={{ background:GV.blanc, border:`1px solid ${GV.border}`, borderRadius:14, padding:"20px 24px", marginBottom:24 }}>
                <div style={{ fontSize:12, fontWeight:800, color:GV.vertDark, textTransform:"uppercase", letterSpacing:1.5, marginBottom:14 }}>
                  📊 Comparaison — Coût total estimé × Score de compromis global
                </div>
                <div style={{ overflowX:"auto" }}>
                  <svg width={W} height={svgH} style={{ display:"block", minWidth:500 }}>
                    {resultats.map((sol, i) => {
                      const info = CATALOGUE_COMP[sol.type];
                      const barW = Math.max(4, Math.round(((sol.cout_total / maxCout) * (W - paddingL - paddingR))));
                      const y = i * (barH + gap) + 20;
                      // Couleur : vert (secu élevé) → orange (secu bas)
                      const ratio = sol.score_secu / 100;
                      const r = Math.round(45 + (1 - ratio) * 187);
                      const g = Math.round(122 + ratio * 100 - (1-ratio)*70);
                      const b = Math.round(45 - (1 - ratio) * 45);
                      const color = `rgb(${r},${g},${b})`;
                      return (
                        <g key={sol.type}>
                          {/* Label solution */}
                          <text x={paddingL - 8} y={y + barH/2 + 4} textAnchor="end" fontSize={11} fontWeight={i===0?"800":"400"} fill={i===0?GV.vertDark:GV.dark}>
                            {info.icone} {info.nom.length > 22 ? info.nom.slice(0,22)+"…" : info.nom}
                          </text>
                          {/* Barre */}
                          <rect x={paddingL} y={y} width={barW} height={barH} rx={5} fill={color} opacity={0.85}/>
                          {/* Étiquette coût */}
                          <text x={paddingL + barW + 6} y={y + barH/2 + 4} fontSize={10} fill={GV.dark} fontWeight={700}>
                            {sol.cout_total.toLocaleString("fr-FR")} €
                          </text>
                          {/* Badge score */}
                          <rect x={paddingL + barW - 38} y={y + 4} width={34} height={barH - 8} rx={4} fill="rgba(255,255,255,0.7)"/>
                          <text x={paddingL + barW - 21} y={y + barH/2 + 4} textAnchor="middle" fontSize={10} fontWeight={800} fill={color}>
                            {sol.score_global}%
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                <div style={{ fontSize:10, color:GV.grisMid, marginTop:8 }}>
                  Couleur : 🟢 Fiabilité maximale → 🟠 Économie maximale · Étiquette : compromis global (%)
                  {params.nappe && " · ✓ Majoration +15% nappe incluse"}
                </div>
              </div>
            );
          })()}

          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(230px, 1fr))", gap:16, marginBottom:28 }}>
            {resultats.map((sol,idx) => {
              const info = CATALOGUE_COMP[sol.type];
              const isReco = idx===0 && resultats.length > 1;
              return (
                <div key={sol.type} style={{ background:isReco?`linear-gradient(135deg,${info.couleur}10,${GV.blanc})`:GV.blanc, border:`2px solid ${isReco?info.couleur:GV.border}`, borderRadius:14, overflow:"hidden", position:"relative", boxShadow:isReco?`0 4px 20px ${info.couleur}20`:"0 1px 6px rgba(0,0,0,0.05)" }}>
                  {isReco && <div style={{ position:"absolute", top:10, right:10, background:info.couleur, color:GV.blanc, fontSize:9, fontWeight:800, letterSpacing:1.5, padding:"3px 8px", borderRadius:4 }}>{t("comparaison_page.recommande_badge")}</div>}
                  <div style={{ padding:"16px 16px 10px", borderBottom:`1px solid ${GV.border}` }}>
                    <div style={{ fontSize:26, marginBottom:5 }}>{info.icone}</div>
                    <div style={{ fontWeight:800, fontSize:14, color:GV.dark, marginBottom:4 }}>{getCatalogueNom(sol.type)}</div>
                    <div style={{ fontSize:10, color:GV.grisMid, marginBottom:8 }}>{info.range}</div>
                    {moduleTag(sol.type)}
                    {FAMILLES_TECHNOLOGIES_DISTINCTES[sol.type] && (
                      <div style={{ marginTop:8, padding:"6px 10px", background:"#fef3c7", border:"1px solid #fcd34d", borderRadius:4, fontSize:11, color:"#92400e", lineHeight:1.5 }}>
                        ℹ {t(FAMILLES_TECHNOLOGIES_DISTINCTES[sol.type])}
                      </div>
                    )}
                  </div>
                  <div style={{ padding:"12px 16px" }}>
                    {[[t("comparaison_page.cout_total"),`${sol.cout_total.toLocaleString("fr-FR")} ${devise}`],[t("comparaison_page.cout_ml"),`${sol.cout_ml.toLocaleString("fr-FR")} ${devise}/lm`]].map(([l,v])=>(
                      <div key={l} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderBottom:`1px solid ${GV.border}`, fontSize:12 }}>
                        <span style={{ color:GV.grisMid }}>{l}</span>
                        <span style={{ fontWeight:700, color:GV.dark }}>{v}</span>
                      </div>
                    ))}
                    {[[t("comparaison_page.score_cout"),sol.score_cout,GV.bleu],[t("comparaison_page.score_secu"),sol.score_secu,GV.vert],[t("comparaison_page.score_global"),sol.score_global,info.couleur]].map(([lbl,val,col])=>(
                      <div key={lbl} style={{ marginTop:8 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, letterSpacing:0.8, marginBottom:3 }}>
                          <span style={{ color:GV.grisMid, fontWeight:700 }}>{lbl}</span>
                          <span style={{ color:col, fontWeight:800 }}>{val}%</span>
                        </div>
                        <div style={{ height:5, background:GV.border, borderRadius:3, overflow:"hidden" }}>
                          <div style={{ width:`${val}%`, height:"100%", background:col, borderRadius:3, transition:"width 1s ease" }}/>
                        </div>
                      </div>
                    ))}
                    {sol.alertes.length>0 && (
                      <div style={{ marginTop:10, padding:"8px 10px", borderRadius:6, background:sol.alertes[0].startsWith("✅")?GV.vertLight:"#FFF7ED", border:`1px solid ${sol.alertes[0].startsWith("✅")?GV.vertMid:"#F59E0B"}` }}>
                        {sol.alertes.map((a,i)=><div key={i} style={{ fontSize:11, lineHeight:1.6, color:a.startsWith("✅")?GV.vertDark:a.startsWith("⚠")?GV.orange:GV.grisMid }}>{a}</div>)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Tableau comparatif */}
          <div style={{ background:GV.blanc, border:`1px solid ${GV.border}`, borderRadius:14, overflow:"hidden", marginBottom:20 }}>
            <div style={{ padding:"13px 20px", borderBottom:`1px solid ${GV.border}`, background:GV.gris }}>
              <div style={{ fontSize:12, color:GV.vertDark, fontWeight:800, letterSpacing:2, textTransform:"uppercase" }}>{t("comparaison_page.tableau_titre")}</div>
            </div>
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <thead>
                  <tr style={{ background:GV.gris }}>
                    <th style={{ padding:"10px 16px", textAlign:"left", color:GV.grisMid, fontWeight:700, fontSize:11 }}>{t("comparaison_page.critere")}</th>
                    {resultats.map(sol => {
                      const info = CATALOGUE_COMP[sol.type];
                      return <th key={sol.type} style={{ padding:"10px 12px", textAlign:"center", color:info.couleur, fontWeight:800, fontSize:11 }}>{info.icone} {getCatalogueNom(sol.type)}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {construireTableau(resultats, pays, t).map(([crit,vals])=>(
                    <tr key={crit} style={{ borderBottom:`1px solid ${GV.border}` }}>
                      <td style={{ padding:"9px 16px", color:GV.grisMid, fontWeight:600 }}>{crit}</td>
                      {vals.map((v,i)=>(
                        <td key={i} style={{ padding:"9px 12px", textAlign:"center", fontWeight:v.includes("✅")||v.includes("⚠")?700:400, color:v.includes("✅")?GV.vert:v.includes("⚠")?GV.orange:v.includes("❌")?GV.rouge:GV.dark }}>{v}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Prix référence */}
          <div style={{ background:GV.gris, borderRadius:10, padding:"14px 18px", border:`1px solid ${GV.border}` }}>
            <div style={{ fontSize:11, color:GV.grisMid, letterSpacing:1.5, fontWeight:700, textTransform:"uppercase", marginBottom:10 }}>
              {t("comparaison_page.prix_ref")} — {normeRef}
            </div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              {(isEuropeNorm
                ? [["Béton C12/15","140 EUR/m³"],["Béton C20/25","220 EUR/m³"],["Acier B500","1.30 EUR/kg"],["Coffrage","45 EUR/m²"],["Gabion (cage)","85 EUR/m³"],["Pierre gabion","35 EUR/m³"],["Terrassement","18 EUR/m³"]]
                : [["Béton C12/15","7 000 DZD/m³"],["Béton C20/25","30 000 DZD/m³"],["Acier B500","98 DZD/kg"],["Coffrage","580 DZD/m²"],["Cage gabion","3 850 DZD"],["Pierre gabion","2 500 DZD/m³"],["Terrassement","400 DZD/m³"]]
              ).map(([k,v])=>(
                <div key={k} style={{ background:GV.blanc, border:`1px solid ${GV.border}`, borderRadius:6, padding:"5px 10px" }}>
                  <span style={{ color:GV.grisMid, fontSize:10 }}>{k} </span>
                  <span style={{ color:GV.vert, fontWeight:800, fontSize:11 }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ fontSize:11, color:GV.grisMid, marginTop:10 }}>
              {t("comparaison_page.prix_indicatifs")}
            </div>
          </div>
        </>)}
      </div>
    </div>
  );
}

// ─── Calcul des résultats depuis les données brutes de l'API ─────────────────
function calculerResultats(apiSolutions, prixRefApi, prixCurrent, params, modeAffichage, typeOuvrageProjet, typesProjet) {
  let typesACComparer = Object.keys(apiSolutions);
  if (modeAffichage === "normal" && typesProjet?.length > 0) {
    const famillesAutorisees = FILTRE_PAR_OUVRAGE[typeOuvrageProjet] || [];
    const intersection = typesProjet.filter(t => famillesAutorisees.includes(t));
    if (intersection.length > 0) {
      typesACComparer = typesACComparer.filter(t => intersection.includes(t));
    }
  }
  const solutions = typesACComparer.map(sol_type => {
    const apiSol = apiSolutions[sol_type];
    if (!apiSol || !CATALOGUE_COMP[sol_type]) return null;
    const prixBase = prixRefApi[sol_type] || 1;
    const prixAdj  = prixCurrent[sol_type] || prixBase;
    const ratio    = prixBase > 0 ? prixAdj / prixBase : 1;
    return {
      type:       sol_type,
      cout_total: Math.round(apiSol.cout_total * ratio),
      cout_ml:    Math.round(apiSol.cout_ml    * ratio),
      score_secu: apiSol.score_secu,
      alertes:    apiSol.alertes,
    };
  }).filter(Boolean);

  if (solutions.length === 0) return [];

  const maxC = Math.max(...solutions.map(s => s.cout_total));
  const w = params.priorite === "securite" ? [0.25, 0.75]
          : params.priorite === "economie" ? [0.75, 0.25]
          :                                  [0.5,  0.5];

  return solutions.map(s => {
    const score_cout   = Math.round((1 - s.cout_total / maxC) * 100);
    const score_global = Math.round(score_cout * w[0] + s.score_secu * w[1]);
    return { ...s, score_cout, score_global };
  }).sort((a, b) => b.score_global - a.score_global);
}

// ─── Données tableau par type de solution ─────────────────────────────────────
function construireTableau(resultats, pays, t) {
  const DONNEES = {
    murs_cantilever: {
      "Structure":        "Béton armé C20/25",
      "Ferraillage":      "Oui — B500S",
      "Séisme":           pays==="france" ? "✅ EC8 NF EN 1998-5" : "✅ Onglet Séisme GEO5",
      "Drainage":         "Drain géotextile requis",
      "Emprise":          "Modérée (semelle)",
      "Durée chantier":   "Moyenne (coffrages)",
      "Esthétique":       "Standard",
      "Norme":            pays==="france" ? "EC2 / EC7 / NF P94-281" : "DTR BC 2.33",
    },
    murs_poids: {
      "Structure":        pays==="france" ? "Maçonnerie / Béton cyclopéen" : "Béton cyclopéen C12/15",
      "Ferraillage":      "Non — poids propre",
      "Séisme":           "⚠ Résistance sismique limitée",
      "Drainage":         "Barbacanes recommandées",
      "Emprise":          "Large (base H×0.7)",
      "Durée chantier":   "Longue ⚠",
      "Esthétique":       "Standard / Rustique",
      "Norme":            pays==="france" ? "DTU / NF EN 1996" : "DTR BC 2.33",
    },
    gabions: {
      "Structure":        "Paniers métalliques + pierres",
      "Ferraillage":      "Non",
      "Séisme":           "✅ Flexible — bon comportement",
      "Drainage":         "✅ Drainage naturel — idéal nappe",
      "Emprise":          "Modérée",
      "Durée chantier":   "Courte ✅",
      "Esthétique":       "Naturel ✅ — intégration paysagère",
      "Norme":            pays==="france" ? "NF EN 10223-3 / CEREMA" : "Recommandations CNERIB",
    },
    pentes_clouees: {
      "Structure":        "Clous acier + béton projeté",
      "Ferraillage":      "Clous — acier haute adhérence",
      "Séisme":           "✅ Excellent comportement sismique",
      "Drainage":         "Géotextile drainant requis",
      "Emprise":          "Faible ✅",
      "Durée chantier":   pays==="france" ? "Longue — mission G3 requise ⚠" : "Longue ⚠",
      "Esthétique":       "Naturel ✅",
      "Norme":            pays==="france" ? "NF EN 14490 / CLOUTERRE" : "Recommandations LCPC",
    },
    ecrans: {
      "Structure":        "Palplanches / Berlinoise / Paroi moulée",
      "Ferraillage":      "Oui (paroi armée)",
      "Séisme":           "✅ Très bonne tenue sismique",
      "Drainage":         "Pompage ou géotextile",
      "Emprise":          "Très faible ✅ (exécution en place)",
      "Durée chantier":   "Variable selon type",
      "Esthétique":       "Technique",
      "Norme":            pays==="france" ? "NF P94-282 / EC7" : "DTR BC 2.33.3",
    },
    talutage: {
      "Structure":        "Terrassement + végétalisation",
      "Ferraillage":      "Non",
      "Séisme":           "⚠ SF réduit si pente raide",
      "Drainage":         "Fossé + géotextile requis",
      "Emprise":          "⚠ Très large (H×1.5 min.)",
      "Durée chantier":   "Courte ✅",
      "Esthétique":       "Naturel ✅",
      "Norme":            pays==="france" ? "NF EN 1997-1 §11" : "DTR BC 2.33",
    },
    pieux_secants: {
      "Structure":        "Paroi de pieux sécants BA",
      "Ferraillage":      "Oui — B500S + cage armature",
      "Séisme":           "✅ Excellente tenue sismique",
      "Drainage":         "Étanche — pompage si nappe",
      "Emprise":          "Très faible ✅ (milieu urbain)",
      "Durée chantier":   "⚠ Longue — mission G3 obligatoire",
      "Esthétique":       "Technique",
      "Norme":            pays==="france" ? "NF P 94-262 + NF P 94-270 §9" : "DTR BC 2.33.3",
    },
    pieux: {
      "Structure":        "Pieux forés béton armé",
      "Ferraillage":      "Oui — cage armature",
      "Séisme":           "✅ Très bonne tenue — NF EN 1998-5",
      "Drainage":         "Sans objet (fondation profonde)",
      "Emprise":          "Faible ✅",
      "Durée chantier":   "Moyenne",
      "Esthétique":       "Sans objet",
      "Norme":            pays==="france" ? "NF P 94-262 + EC7 DA2*" : "DTR BC 2.33.4",
    },
    semelles: {
      "Structure":        "Béton armé C20/25",
      "Ferraillage":      "Oui — B500S",
      "Séisme":           "⚠ Vérification EC8 §5 obligatoire",
      "Drainage":         "Sans objet",
      "Emprise":          "Modérée",
      "Durée chantier":   "Courte ✅",
      "Esthétique":       "Sans objet",
      "Norme":            pays==="france" ? "NF P 94-261 + EC7 DA2*" : "DTR BC 2.33.2",
    },
    micropieux: {
      "Structure":        "Micropieux forés + coulis injection",
      "Ferraillage":      "Tube acier + armature",
      "Séisme":           "✅ Très bonne tenue — NF EN 1998-5",
      "Drainage":         "Sans objet",
      "Emprise":          "Très faible ✅ (reprise sous-oeuvre)",
      "Durée chantier":   "Moyenne — phases alternées",
      "Esthétique":       "Sans objet",
      "Norme":            pays==="france" ? "NF P 94-262 §14 + DTU 13.2" : "DTR BC 2.33.4",
    },
  };

  const critKeys = ["structure","ferraillage","seisme","drainage","emprise","duree_chantier","esthetique","norme"];
  const critFr   = ["Structure","Ferraillage","Séisme","Drainage","Emprise","Durée chantier","Esthétique","Norme"];
  return critKeys.map((key, idx) => {
    const label = t ? t(`comparaison_page.tableau_criteres.${key}`, { defaultValue: critFr[idx] }) : critFr[idx];
    const frLabel = critFr[idx];
    return [
      label,
      resultats.map(sol => (DONNEES[sol.type] || {})[frLabel] || "—")
    ];
  });
}

export default TabComparaison;
